import React, { useEffect, lazy, Suspense, useState } from 'react';
import { initializePerformanceMonitoring } from '@/utils/performanceMonitor';

// Lazily load these components since they're not needed on initial render
const PerformanceDisplay = lazy(() => import('./diagnostics/PerformanceDisplay'));

/**
 * This component handles page performance optimizations:
 * - Initializes performance monitoring
 * - Provides preloading hints for critical resources
 * - Implements route-based code splitting
 * - Tracks user interactions and page responsiveness
 */
const PagePerformance: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<any>(null);

  useEffect(() => {
    // Initialize performance monitoring with callback
    const monitor = initializePerformanceMonitoring();
    
    // Track initial page load metrics
    const collectInitialMetrics = () => {
      const metrics = monitor.getMetrics();
      setPerformanceData(metrics);
      console.log('Initial page performance metrics collected');
    };
    
    // Dynamically add preload hints for critical resources based on current route
    const addPreloadHints = () => {
      // Determine current path and add relevant preloads
      const currentPath = window.location.pathname;
      
      if (currentPath === '/') {
        // On homepage, preload auth and dashboard as they're likely next
        preloadResource('/auth', 'prefetch');
        preloadResource('/dashboard', 'prefetch');
      } else if (currentPath === '/auth') {
        // On auth page, preload dashboard
        preloadResource('/dashboard', 'prefetch');
      } else if (currentPath === '/dashboard') {
        // On dashboard, preload likely destinations
        preloadResource('/tools/content', 'prefetch');
        preloadResource('/community', 'prefetch');
      }
    };
    
    // Helper to add preload/prefetch link tags
    const preloadResource = (path: string, rel: 'preload' | 'prefetch') => {
      // Check if this preload already exists
      const existingLinks = document.querySelectorAll(`link[href="${path}"]`);
      if (existingLinks.length > 0) return;
      
      const link = document.createElement('link');
      link.rel = rel;
      link.href = path;
      link.as = 'document';
      document.head.appendChild(link);
    };
    
    // Add preload hints for critical resources
    addPreloadHints();
    
    // Initialize intersection observer for lazy loading
    const initLazyLoading = () => {
      if ('IntersectionObserver' in window) {
        const lazyImages = document.querySelectorAll('img[loading="lazy"]');
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              img.src = img.dataset.src || '';
              img.classList.remove('lazy');
              imageObserver.unobserve(img);
            }
          });
        });
        
        lazyImages.forEach(image => {
          imageObserver.observe(image);
        });
      }
    };

    // Monitor long tasks for responsiveness issues
    const initLongTaskObserver = () => {
      if ('PerformanceObserver' in window) {
        try {
          const longTaskObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
              // Log tasks that block the main thread for more than 100ms
              if (entry.duration > 100) {
                console.warn('Long task detected:', {
                  duration: `${Math.round(entry.duration)}ms`,
                  startTime: entry.startTime,
                  entryType: entry.entryType,
                  name: entry.name
                });
              }
            });
          });
          
          longTaskObserver.observe({ entryTypes: ['longtask'] });
        } catch (error) {
          console.error('LongTask observer not supported', error);
        }
      }
    };

    // Monitor network requests for slow resources
    const initResourceObserver = () => {
      if ('PerformanceObserver' in window) {
        try {
          const resourceObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
              const resource = entry as PerformanceResourceTiming;
              // Flag slow resources (taking more than 1s to load)
              if (resource.duration > 1000) {
                console.warn('Slow resource detected:', {
                  name: resource.name,
                  duration: `${Math.round(resource.duration)}ms`,
                  size: resource.transferSize ? `${Math.round(resource.transferSize / 1024)}KB` : 'Unknown',
                  type: resource.initiatorType
                });
              }
            });
          });
          
          resourceObserver.observe({ entryTypes: ['resource'] });
        } catch (error) {
          console.error('Resource observer not supported', error);
        }
      }
    };

    // Track client-side navigations for SPA metrics
    const initNavigationObserver = () => {
      if ('PerformanceObserver' in window) {
        try {
          const navObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
              console.log('Navigation performance:', {
                type: entry.entryType,
                name: entry.name,
                startTime: entry.startTime,
                duration: entry.duration
              });
              
              // Update metrics after navigation
              setTimeout(() => {
                const metrics = monitor.getMetrics();
                setPerformanceData(metrics);
              }, 1000);
            });
          });
          
          navObserver.observe({ entryTypes: ['navigation'] });
        } catch (error) {
          console.error('Navigation observer not supported', error);
        }
      }
    };
    
    // Initialize lazy loading after the page loads
    if (document.readyState === 'complete') {
      initLazyLoading();
    } else {
      window.addEventListener('load', initLazyLoading);
    }
    
    // Collect metrics after page load
    window.addEventListener('load', () => {
      // Collect metrics after a slight delay to capture more accurate data
      setTimeout(collectInitialMetrics, 1000);
    });
    
    return () => {
      window.removeEventListener('load', initLazyLoading);
    };
  }, []);
  
  return null; // This component doesn't render anything visible
};

export default PagePerformance;
